<template>
    <AppModal ref="Modal" :title="$t('labels.copy_products_content_from_integration')" @on-hide="hide" size="full">
        <div class="modal-body">
            <template v-if="fetchTask.state.isRunning">
                <TaskProgressBar :done="fetchTask.state.done" :total="fetchTask.state.total"/>
            </template>
            <template v-else-if="!fetchingFromSourceIntegrationIsCompleted && (!sourceIntegration || !fetchTask.state.isRunning)">
                <div class="alert alert-info border-0 alert-dismissible" v-if="sourceProductsNotDefined">
                    <button type="button" class="close" data-dismiss="alert"><span>×</span></button>
                    {{ $t('alerts.products_not_found') }}
                </div>
                <FormGroup :label="$t('labels.integration')" required>
                    <AppAutoComplete
                        :url="`/projects/${$route.params.projectUuid}/pim/channels`"
                        :model-value="sourceIntegration?.uuid"
                        :selected-value="sourceIntegration?.name"
                        :query-params="{
                            ignore_uuids: [this.$route.params.integrationUuid],
                        }"
                        @update:model-value="integration => sourceIntegration = integration"
                    />
                </FormGroup>
                <div class="row" v-if="allChecked && currentIntegrationProductUuids.length < totalProducts">
                    <div class="col-lg-6">
                        <InputGroup>
                            <Radio :model-value="copyOption === 'selected'" @update:modelValue="copyOption = 'selected'"/>
                            <template #append>
                                {{ $t('labels.copy_only_selected_products', {total: currentIntegrationProductUuids.length}) }}
                            </template>
                        </InputGroup>
                    </div>
                    <div class="col-lg-6">
                        <InputGroup>
                            <Radio :model-value="copyOption === 'all'" @update:modelValue="copyOption = 'all'"/>
                            <template #append>
                                {{ $t('labels.copy_all_products', {total: totalProducts}) }}
                            </template>
                        </InputGroup>
                    </div>
                </div>
            </template>
            <template v-else-if="copyTask.state.isRunning">
                <TaskProgressBar :done="copyTask.state.done" :total="copyTask.state.total"/>
            </template>
            <template v-else-if="copyBadHttpHistory.length > 0">
                <div class="alert alert-warning border-0 alert-dismissible mt-3">
                    <button type="button" class="close" data-dismiss="alert"><span>×</span></button>
                    <span class="font-weight-semibold">{{ $t('labels.copy_products_warning_title') }}</span>
                    <p>{{ $t('labels.copy_products_warning_message') }}</p>
                    <p>{{ $t('labels.copy_products_warning_manual') }}</p>
                    <p>{{ $t('labels.copy_products_error_details') }}</p>
                </div>
                <div class="table-responsive table-scrollable">
                    <table class="table table-bordered table-fixed">
                        <thead>
                            <tr>
                                <th>{{ $t('labels.copy_products_request') }}</th>
                                <th>{{ $t('labels.copy_products_response') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="badHttpHistoryItem in copyBadHttpHistory">
                                <td><span class="text-nowrap">{{ badHttpHistoryItem.request.body }}</span></td>
                                <td><span class="text-nowrap">{{ badHttpHistoryItem.response.body }}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
            <template v-else>
                <div class="alert alert-warning border-0 alert-dismissible" v-if="servicesAreDifferent">
                    <button type="button" class="close" data-dismiss="alert"><span>×</span></button>
                    {{ $t('labels.copy_products_different_services_warning') }}
                </div>
                <p>{{ $t('labels.copy_products_table_title') }}</p>
                <p>{{ $t('labels.copy_products_content_table_description') }}</p>
                <p>{{ $t('labels.copy_products_table_columns_title') }}</p>
                <ul class="list">
                    <li>{{ $t('labels.copy_products_table_update') }}</li>
                    <li>{{ $t('labels.copy_products_table_force_update') }}</li>
                </ul>
                <template v-if="multipleSourceProducts.length > 0">
                    <p class="font-weight-semibold mb-1">{{ $t('labels.copy_products_content_multiple_title') }}</p>
                    <p>{{ $t('labels.copy_products_content_multiple_description') }}</p>
                    <div class="alert alert-warning border-0" v-if="unresolvedSourceProductsCount > 0">
                        {{ $t('labels.copy_products_content_multiple_unresolved', {total: unresolvedSourceProductsCount}) }}
                    </div>
                    <div class="mb-2">
                        <AppButton color="light" @click="resetSourceProducts">
                            {{ $t('labels.copy_products_content_reset_sources') }}
                        </AppButton>
                    </div>
                    <div class="table-responsive mb-3">
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th>{{ $t('labels.copy_products_content_publication_label') }}</th>
                                    <th>{{ $t('labels.copy_products_content_source_publications_label') }}</th>
                                    <th>{{ $t('labels.copy_products_content_source_label') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="product in multipleSourceProducts" :key="product.uuid">
                                    <tr v-for="(sourceProduct, index) in product.sourceProducts" :key="sourceProduct.uuid">
                                        <td v-if="index === 0" :rowspan="product.sourceProducts.length" class="align-middle">
                                            {{ publicationTitle(product) }}
                                            <br>
                                            <span class="text-muted">{{ product.sku }}</span>
                                        </td>
                                        <td>
                                            <router-link
                                                target="_blank"
                                                :to="{name: 'Product', params: {integrationUuid: sourceIntegration.uuid, integrationProductUuid: sourceProduct.uuid}}"
                                            >
                                                {{ publicationTitle(sourceProduct) }}
                                            </router-link>
                                            <br>
                                            <span class="text-muted">{{ sourceProduct.sku }}</span>
                                        </td>
                                        <td>
                                            <Radio
                                                :model-value="product.sourceProduct?.uuid === sourceProduct.uuid"
                                                @update:model-value="product.sourceProduct = sourceProduct"
                                            />
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </template>
                <div class="table-responsive">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>{{ $t('labels.copy_products_field_label') }}</th>
                                <th>{{ $t('labels.copy_products_update_label') }}</th>
                                <th>{{ $t('labels.copy_products_force_update_label') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th>{{ $t('labels.copy_products_select_all') }}</th>
                                <th>
                                    <AppCheckbox
                                        type="checkbox"
                                        :model-value="allFieldsSelectedForUpdate"
                                        @update:model-value="checkAllFields('update', $event)"
                                        :disabled="everyProductHasntDestinationProduct"
                                    />
                                </th>
                                <th>
                                    <AppCheckbox
                                        type="checkbox"
                                        :model-value="allFieldsSelectedForForceUpdate"
                                        @update:model-value="checkAllFields('forceUpdate', $event)"
                                        :disabled="everyProductHasntDestinationProduct"
                                    />
                                </th>
                            </tr>
                            <tr v-for="(field, index) in fields" :key="index">
                                <td>{{ field.title }}</td>
                                <td>
                                    <AppCheckbox
                                        type="checkbox"
                                        v-model="field.update"
                                        :disabled="isFieldDisabled(field)"
                                    />
                                </td>
                                <td>
                                    <AppCheckbox
                                        type="checkbox"
                                        v-model="field.forceUpdate"
                                        :disabled="isFieldDisabled(field)"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </div>
        <div class="modal-footer" v-if="!fetchTask.state.isRunning">
            <AppButton color="indigo" @click="getProductsData" :disabled="!sourceIntegration" v-if="!fetchingFromSourceIntegrationIsCompleted">
                {{ $t('buttons.continue') }}
            </AppButton>
            <AppButton color="indigo" @click="copyProductsFromSource" v-if="fetchingFromSourceIntegrationIsCompleted" :disabled="!someOneFieldSelected">
                {{ $t('buttons.copy') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../components/new/modal/AppModal.vue';
import FormGroup from '../../../../components/new/form/group/FormGroup.vue';
import AppAutoComplete from '../../../../components/new/form/autocomplete/AppAutoComplete.vue';
import AppButton from '../../../../components/new/button/AppButton.vue';
import TaskProgressBar from '../../../../components/new/progress/TaskProgressBar.vue';
import AppCheckbox from '../../../../components/new/form/checkbox/AppCheckbox.vue';
import Radio from '../../../../components/new/form/radio/Radio.vue';
import InputGroup from '../../../../components/new/form/InputGroup.vue';

import { httpClient } from '../../../../services/HttpClient';
import HttpRequest from '../../../../services/Http/HttpRequest';
import HttpSender from '../../../../services/Http/HttpSender';
import { chunk } from '../../../../utils';
import MessageNotificator from '../../../../services/MessageNotificator';
import { createProgressTask } from '../../../../services/Progress/ProgressTask';

const SERVICE_SPECIFIC_FIELD_KEYS = ['source_id', 'second_source_id'];

export default {
    name: "CopyProductsContentFromIntegrationModal",
    components: {
        AppModal, FormGroup, AppAutoComplete, AppButton, TaskProgressBar, AppCheckbox, Radio, InputGroup
    },
    props: {
        integration: {
            type: Object,
            required: true
        }
    },
    emits: ['product-copied'],
    data() {
        return {
            currentIntegrationProducts: [],
            currentIntegrationProductUuids: [],
            sourceIntegration: null,
            fetchingFromSourceIntegrationIsCompleted: false,
            fields: [
                { key: 'source_id',         update: false, forceUpdate: false, title: this.$t('labels.source_id') },
                { key: 'second_source_id',  update: false, forceUpdate: false, title: this.$t('labels.second_source_id') },
                { key: 'sku',               update: false, forceUpdate: false, title: this.$t('labels.sku') },
                { key: 'names.uk',          update: false, forceUpdate: false, title: `${this.$t('labels.name')} (${this.$t('languages.uk')})` },
                { key: 'names.ru',          update: false, forceUpdate: false, title: `${this.$t('labels.name')} (${this.$t('languages.ru')})` },
                { key: 'descriptions.uk',   update: false, forceUpdate: false, title: `${this.$t('labels.description')} (${this.$t('languages.uk')})` },
                { key: 'descriptions.ru',   update: false, forceUpdate: false, title: `${this.$t('labels.description')} (${this.$t('languages.ru')})` },
                { key: 'seos.uk',           update: false, forceUpdate: false, title: `${this.$t('labels.seo')} (${this.$t('languages.uk')})` },
                { key: 'seos.ru',           update: false, forceUpdate: false, title: `${this.$t('labels.seo')} (${this.$t('languages.ru')})` },
                { key: 'images',            update: false, forceUpdate: false, title: this.$t('labels.images') },
                { key: 'group_key',         update: false, forceUpdate: false, title: this.$t('labels.group_key') },
                { key: 'manufacturer_name', update: false, forceUpdate: false, title: this.$t('labels.manufacturer_name') },
                { key: 'price',             update: false, forceUpdate: false, title: this.$t('labels.price') },
                { key: 'sale_price',        update: false, forceUpdate: false, title: this.$t('labels.sale_price') },
                { key: 'balance',           update: false, forceUpdate: false, title: this.$t('labels.balance') }
            ],
            fetchTask: createProgressTask(),
            copyTask: createProgressTask(),
            copyBadHttpHistory: [],
            httpSender: new HttpSender(),
            sourceProductsNotDefined: false,
            filters: {},
            allChecked: false,
            totalProducts: 0,
            copyOption: 'selected'
        };
    },
    methods: {
        show(productsUuids, filters, allChecked, totalProducts) {
            this.currentIntegrationProductUuids = productsUuids;
            this.filters = filters || {};
            this.allChecked = allChecked || false;
            this.totalProducts = totalProducts || 0;
            this.$refs.Modal.show();
        },
        hide() {
            this.fetchTask.cancel();
            this.copyTask.cancel();
            this.$refs.Modal.hide();
            this.sourceIntegration = null;
            this.currentIntegrationProductUuids = [];
            this.currentIntegrationProducts = [];
            this.fetchingFromSourceIntegrationIsCompleted = false;
            this.sourceCategory = null;
            this.sourceProductsNotDefined = false;
            this.copyBadHttpHistory = [];
            this.httpSender = new HttpSender();
            this.filters = {};
            this.allChecked = false;
            this.totalProducts = 0;
            this.copyOption = 'selected';
            for (let field of ['update', 'forceUpdate']) {
                this.checkAllFields(field, false);
            }
        },
        async getProductsData() {
            this.sourceProductsNotDefined = false;

            const completed = await this.fetchTask.run(async (progress) => {
                const products = await this.collectCurrentIntegrationProducts(progress);

                if (!progress.signal.aborted) {
                    this.currentIntegrationProducts = products;
                }
            });

            if (!completed) {
                return;
            }

            if (this.currentIntegrationProducts.every(product => product.sourceProducts.length === 0)) {
                this.sourceProductsNotDefined = true;
                return;
            }

            this.fetchingFromSourceIntegrationIsCompleted = true;
        },
        async collectCurrentIntegrationProducts(progress) {
            let currentIntegrationProducts = this.copyOption === 'all'
                ? await this.fetchCurrentIntegrationProductsByFilters(progress)
                : await this.fetchIntegrationProducts('uuids', this.currentIntegrationProductUuids, this.$route.params.integrationUuid, progress);

            let internalProductUuids = currentIntegrationProducts
                .filter(currentIntegrationProduct => currentIntegrationProduct.internalProductUuid)
                .map(currentIntegrationProduct => currentIntegrationProduct.internalProductUuid);
            let sourceIntegrationProductsFilteredByInternalProductUuid = await this.fetchIntegrationProducts('internal_product_uuids', internalProductUuids, this.sourceIntegration.uuid, progress);
            let sourceIntegrationProductsGroupedByInternalProductUuid = this.groupAllBy(sourceIntegrationProductsFilteredByInternalProductUuid, 'internalProductUuid');

            let currentIntegrationProductSkus = currentIntegrationProducts
                .filter(currentIntegrationProduct => {
                    if (!currentIntegrationProduct.internalProductUuid) {
                        return true;
                    } else {
                        return !sourceIntegrationProductsGroupedByInternalProductUuid[currentIntegrationProduct.internalProductUuid];
                    }
                })
                .map(currentIntegrationProduct => currentIntegrationProduct.sku);
            let sourceIntegrationProductsFilteredBySku = await this.fetchIntegrationProducts('skus', currentIntegrationProductSkus, this.sourceIntegration.uuid, progress);
            let sourceIntegrationProductsGroupedBySku = this.groupBy(sourceIntegrationProductsFilteredBySku, 'sku');

            return this.setSourceProduct(currentIntegrationProducts, sourceIntegrationProductsGroupedByInternalProductUuid, sourceIntegrationProductsGroupedBySku);
        },
        async fetchCurrentIntegrationProductsByFilters({tick}) {
            let integrationUuid = this.$route.params.integrationUuid,
                perPage = 10,
                products = [],
                requests = [];

            let totalResponse = await httpClient.get(`/integrations/${integrationUuid}/products`, {
                ...this.filters,
                limit: 1
            });
            let totalPages = Math.ceil(totalResponse.data.meta.total / perPage);

            for (let page = 1; page <= totalPages; page++) {
                requests.push(new HttpRequest("GET", `/integrations/${integrationUuid}/products`, {
                    ...this.filters,
                    limit: perPage,
                    page: page,
                    extra_fields: ['integrationFilterItems']
                }));
            }

            await this.httpSender.sendRequests(requests, (request, response) => {
                tick();
                if (response.status !== 200) {
                    return;
                }

                products.push(...response.body.data.map(integrationProduct => this.formatIntegrationProductData(integrationProduct)));
            });

            return products;
        },
        async fetchIntegrationProducts(field, values, integrationUuid, {tick}) {
            let products = [],
                requests = [];

            for (let chunkValues of chunk(values, 10)) {
                requests.push(new HttpRequest("GET", `/integrations/${integrationUuid}/products`, {
                    [field]: chunkValues,
                    extra_fields: ['integrationFilterItems'],
                    limit: 100
                }));
            }

            await this.httpSender.sendRequests(requests, (requests, responses) => {
                tick();
                if (responses.status !== 200) {
                    return;
                }

                products.push(...responses.body.data.map(integrationProduct => this.formatIntegrationProductData(integrationProduct)));
            });
            
            return products;
        },
        formatIntegrationProductData(integrationProduct) {
            return {
                uuid: integrationProduct.uuid,
                source_id: integrationProduct.source_id,
                second_source_id: integrationProduct.second_source_id,
                sku: integrationProduct.sku,
                names: integrationProduct.names,
                descriptions: integrationProduct.descriptions,
                seos: integrationProduct.seo,
                images: integrationProduct.images,
                group_key: integrationProduct.group_key,
                manufacturer_name: integrationProduct.manufacturer_name,
                price: integrationProduct.price,
                sale_price: integrationProduct.sale_price,
                balance: integrationProduct.balance,
                internalProductUuid: integrationProduct.product ? integrationProduct.product.uuid : null,
                sourceProduct: null,
                sourceProducts: [],
            };
        },
        setSourceProduct(integrationProducts, sourceProductsByInternalProductUuid = {}, sourceProductsBySku = {}) {
            let integrationProductsWithSourceProducts = [];

            for (let integrationProduct of integrationProducts) {
                if (integrationProduct.internalProductUuid && sourceProductsByInternalProductUuid[integrationProduct.internalProductUuid]) {
                    integrationProduct.sourceProducts = sourceProductsByInternalProductUuid[integrationProduct.internalProductUuid];
                } else if (sourceProductsBySku[integrationProduct.sku]) {
                    integrationProduct.sourceProducts = [sourceProductsBySku[integrationProduct.sku]];
                }

                if (integrationProduct.sourceProducts.length === 1) {
                    integrationProduct.sourceProduct = integrationProduct.sourceProducts[0];
                }

                integrationProductsWithSourceProducts.push(integrationProduct);
            }

            return integrationProductsWithSourceProducts;
        },
        groupBy(array, key) {
            return array.reduce((result, item) => {
                result[this.getNestedValue(item, key)] = item;
                return result;
            }, {});
        },
        groupAllBy(array, key) {
            return array.reduce((result, item) => {
                (result[this.getNestedValue(item, key)] ??= []).push(item);
                return result;
            }, {});
        },
        resetSourceProducts() {
            this.multipleSourceProducts.forEach(product => product.sourceProduct = null);
        },
        publicationTitle(publication) {
            return publication.names?.uk || publication.names?.ru || publication.sku || publication.uuid;
        },
        getNestedValue(obj, path) {
            return path.split('.').reduce((current, key) => current?.[key], obj);
        },
        setNestedValue(obj, path, value) {
            const keys = path.split('.');
            const lastKey = keys.pop();
            const target = keys.reduce((current, key) => {
                if (!current[key]) current[key] = {};
                return current[key];
            }, obj);
            target[lastKey] = value ?? null;
        },
        checkAllFields(field, check) {
            this.fields.forEach(f => {
                if (check && this.isFieldDisabled(f)) {
                    return;
                }

                f[field] = check;
            });
        },
        isFieldDisabled(field) {
            let disabled = this.everyProductHasntDestinationProduct
                || (this.servicesAreDifferent && SERVICE_SPECIFIC_FIELD_KEYS.includes(field.key));

            return disabled;
        },
        async copyProductsFromSource() {
            const requests = this.currentIntegrationProducts
                .map(currentIntegrationProduct => {
                    return this.buildHttpRequestForProduct(currentIntegrationProduct);
                }).filter(Boolean);

            const completed = await this.copyTask.run(async ({setTotal, tick}) => {
                setTotal(requests.length);

                await this.httpSender.sendRequestsInBatchesSuccessively(requests, () => tick());
            });

            if (!completed) {
                return;
            }

            this.copyBadHttpHistory = this.httpSender.getHistory().filter((historyItem) => {
                return historyItem.response.status !== 201 && historyItem.response.status !== 200;
            });

            if (this.copyBadHttpHistory.length === 0) {
                new MessageNotificator().success(this.$t("alerts.success"));
                this.hide();
                this.$emit('product-copied');
            }
        },
        buildHttpRequestForProduct(currentIntegrationProduct) {
            let source = currentIntegrationProduct.sourceProduct;

            let request = null;

            if (source) {
                request = new HttpRequest('PATCH', `/integrations/products/${currentIntegrationProduct.uuid}`, {});

                for (let field of this.fields) {
                    if (this.isFieldDisabled(field)) {
                        continue;
                    }

                    const sourceValue = this.getNestedValue(source, field.key);
                    const currentValue = this.getNestedValue(currentIntegrationProduct, field.key);

                    if (!sourceValue) {
                        continue;
                    }

                    if (field.forceUpdate || (field.update && !currentValue && sourceValue)) {
                        this.setNestedValue(request.body, field.key, sourceValue);
                    }
                }

                if (Object.keys(request.body).length === 0) {
                    request = null;
                }
            }

            return request;
        }
    },
    computed: {
        servicesAreDifferent() {
            let servicesAreDifferent = Boolean(this.sourceIntegration) && this.sourceIntegration.service !== this.integration.service;

            return servicesAreDifferent;
        },
        selectableFields() {
            let selectableFields = this.fields.filter(field => {
                return !this.servicesAreDifferent || !SERVICE_SPECIFIC_FIELD_KEYS.includes(field.key);
            });

            return selectableFields;
        },
        allFieldsSelectedForUpdate() {
            return this.selectableFields.every(field => field.update);
        },
        allFieldsSelectedForForceUpdate() {
            return this.selectableFields.every(field => field.forceUpdate);
        },
        someOneFieldSelected() {
            return this.selectableFields.some(field => field.update || field.forceUpdate);
        },
        everyProductHasntDestinationProduct() {
            return this.currentIntegrationProducts.every(product => product.sourceProducts.length === 0);
        },
        multipleSourceProducts() {
            return this.currentIntegrationProducts.filter(product => product.sourceProducts.length > 1);
        },
        unresolvedSourceProductsCount() {
            return this.multipleSourceProducts.filter(product => !product.sourceProduct).length;
        }
    },
    beforeUnmount() {
        this.fetchTask.cancel();
        this.copyTask.cancel();
    }
}
</script>